import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { usePermissions } from '../../hooks/usePermissions';
import type { AaiSkill, DeliveryProjectPanel } from '../../types/aai';
import { Action, Card, Chip, Field, Input, Select, StatusChip } from '../../components/aa';
import { relativeTime } from '../partner/format';

/*
 * Where the build stands, and the step that closes the request.
 *
 * Approval hands the work to the Builder. The Builder reports the app running
 * or its review passing and that lands here as Built or Tested. The last step
 * is a person's: publishing the delivered tool against the request, which
 * grants the requester access and tells them it is ready. Until that happens
 * the requester sees "in delivery" and nothing else, so this card exists to make
 * the closing step visible and one click away for the people allowed to take it.
 */

const BUILD_LABEL: Record<string, string> = {
  built: 'Built',
  tested: 'Tested',
};

const sourceLine = (source: string | null | undefined): string => {
  if (!source) return '';
  if (source.includes('@')) return `recorded by ${source}`;
  return `reported by the Builder (${source})`;
};

const toList = (raw: string): string[] =>
  raw.split('\n').map((line) => line.trim()).filter(Boolean);

const TEXTAREA = 'w-full rounded-act border border-input bg-white px-3 py-2 text-[13px] text-ink outline-none transition-colors focus:border-ink focus:ring-[3px] focus:ring-ink/10';

type Mode = 'closed' | 'existing' | 'new';

const emptyListing = {
  name: '',
  problem: '',
  process_steps: '',
  example_input: '',
  example_output: '',
  access_level: 'request',
};

export const DeliveryCard: React.FC<{
  panel: DeliveryProjectPanel;
  onChanged: () => Promise<void>;
}> = ({ panel, onChanged }) => {
  const { can } = usePermissions();
  const { project, tool_skill: tool } = panel;
  /*
   * Who may take the closing step.
   *
   * The programme team may close anything in the company, as they always
   * could. The person NAMED on this request may close this request: naming
   * somebody is how the work is given to them, and an assignment that grants
   * nothing is a label. `aai_deliver_assigned` is read together with the name,
   * never instead of it, which is exactly how the route checks it.
   */
  const me = (localStorage.getItem('user_email') || '').toLowerCase();
  const named = (project.assigned_expert_email || '').toLowerCase();
  const mine = named.length > 0 && named === me;
  const canManage = can('aai_manage_program') || (can('aai_deliver_assigned') && mine);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<Mode>('closed');
  const [skills, setSkills] = useState<AaiSkill[]>([]);
  const [skillId, setSkillId] = useState('');
  const [listing, setListing] = useState(emptyListing);

  /*
   * Opening "describe it here" starts from the request, not from nothing.
   *
   * Everything the listing needs a first draft of is already on the record: the
   * person wrote the title and the detail themselves. Leaving those boxes empty
   * with the request's words as grey placeholder text asks whoever is
   * delivering to retype what is on the screen above them.
   */
  useEffect(() => {
    if (mode !== 'new') return;
    setListing((prev) => {
      if (prev.name || prev.problem) return prev;
      return { ...prev, name: project.title, problem: project.detail || '' };
    });
  }, [mode, project.title, project.detail]);

  useEffect(() => {
    if (mode !== 'existing') return;
    aaiApi.listSkills()
      .then((res) => {
        const free = res.data.skills.filter((s) => !s.use_case_id || s.use_case_id === project.id);
        setSkills(free);
        if (free.length > 0) setSkillId(free[0].id);
      })
      .catch(() => toast.error('Could not load the company skills.'));
  }, [mode, project.id]);

  if (project.status !== 'approved') return null;

  const markBuild = async (state: 'built' | 'tested') => {
    setBusy(true);
    try {
      await aaiApi.setDeliveryBuildState(project.id, state);
      await onChanged();
      toast.success(`Recorded as ${BUILD_LABEL[state].toLowerCase()}.`);
    } catch {
      toast.error('Could not record the build state.');
    } finally {
      setBusy(false);
    }
  };

  const deliver = async () => {
    setBusy(true);
    try {
      if (mode === 'existing') {
        await aaiApi.deliverProject(project.id, { skill_id: skillId });
      } else {
        await aaiApi.deliverProject(project.id, {
          name: listing.name.trim() || project.title,
          problem: listing.problem.trim() || project.detail || project.title,
          process_steps: toList(listing.process_steps),
          example_input: listing.example_input.trim(),
          example_output: listing.example_output.trim(),
          access_level: listing.access_level,
        });
      }
      await onChanged();
      setMode('closed');
      toast.success('Published. The requester has access and has been told their tool is ready.');
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || 'Could not publish the tool.');
    } finally {
      setBusy(false);
    }
  };

  const set = (key: keyof typeof emptyListing) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setListing((prev) => ({ ...prev, [key]: e.target.value }));

  /*
   * What publishing needs, asked here rather than left to the server.
   *
   * A published listing is what another person decides from, so it will not
   * publish without the steps and both examples. The server enforces that and
   * always did; saying it only after the button has been pressed makes the
   * rule look like a failure.
   */
  const blockerFor = (): string => {
    if (mode === 'existing' && !skillId) return 'Pick a skill to link';
    if (mode !== 'new') return '';
    if (!listing.problem.trim() && !project.detail) return 'Say what it solves';
    if (toList(listing.process_steps).length === 0) return 'Add at least one step';
    if (!listing.example_input.trim()) return 'Add an example input';
    if (!listing.example_output.trim()) return 'Add an example output';
    return '';
  };
  const blocker = blockerFor();
  const ready = blocker === '';

  return (
    <Card pad="md">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="text-[13px] font-semibold text-ink">Delivery</span>
          {tool && <StatusChip status="ready" />}
          {!tool && project.build_state && <Chip tone="ok">{BUILD_LABEL[project.build_state]}</Chip>}
          {!tool && !project.build_state && <StatusChip status="in_delivery" label="Building" />}
        </div>
        {project.build_state_at && (
          <span className="text-[12px] text-ink-soft">
            {BUILD_LABEL[project.build_state || '']} {relativeTime(project.build_state_at)} · {sourceLine(project.build_state_source)}
          </span>
        )}
      </div>

      {tool && (
        <p className="m-0 mt-2 text-[12.5px] leading-[20px] text-ink-dim">
          <strong className="text-ink">{tool.name}</strong> is the tool delivered for this request. The requester was granted access and told it is ready.
        </p>
      )}

      {!tool && !project.build_state && (
        <p className="m-0 mt-2 text-[12.5px] leading-[20px] text-ink-dim">
          The Builder reports here when the app is running or its review passes. Nothing has arrived yet.
        </p>
      )}

      {!tool && project.build_state && (
        <p className="m-0 mt-2 text-[12.5px] leading-[20px] text-ink-dim">
          The build is {BUILD_LABEL[project.build_state].toLowerCase()}. The requester still sees it as in delivery until the tool is published against this request.
        </p>
      )}

      {!tool && canManage && mode === 'closed' && (
        <div className="mt-3 flex flex-wrap items-center gap-2.5">
          <Action onClick={() => setMode('existing')} disabled={busy} reason="Saving">Publish the delivered tool</Action>
          {project.build_state !== 'built' && project.build_state !== 'tested' && (
            <Action variant="quiet" onClick={() => markBuild('built')} disabled={busy} reason="Saving">Mark built</Action>
          )}
          {project.build_state !== 'tested' && (
            <Action variant="quiet" onClick={() => markBuild('tested')} disabled={busy} reason="Saving">Mark tested</Action>
          )}
        </div>
      )}

      {!tool && !canManage && (
        <p className="m-0 mt-2 text-[12px] text-ink-soft">Publishing the tool is a program team step.</p>
      )}

      {!tool && canManage && mode !== 'closed' && (
        <div className="mt-4 space-y-3 border-t border-line-soft pt-4">
          <div className="flex flex-wrap gap-2">
            <Action size="sm" variant={mode === 'existing' ? 'primary' : 'quiet'} onClick={() => setMode('existing')}>Link an existing skill</Action>
            <Action size="sm" variant={mode === 'new' ? 'primary' : 'quiet'} onClick={() => setMode('new')}>Describe it here</Action>
          </div>

          {mode === 'existing' && (
            <Field label="Skill" hint="Company skills not yet linked to another request. Publishing links it to this one.">
              <Select value={skillId} onChange={(e) => setSkillId(e.target.value)} disabled={skills.length === 0}>
                {skills.length === 0 && <option value="">No unlinked skills yet</option>}
                {skills.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.published ? '' : ' (draft)'}</option>
                ))}
              </Select>
            </Field>
          )}

          {mode === 'new' && (
            <>
              <Field label="Name">
                <Input value={listing.name} onChange={set('name')} placeholder={project.title} />
              </Field>
              <Field label="What it solves">
                <textarea className={TEXTAREA} rows={2} value={listing.problem} onChange={set('problem')} placeholder={project.detail || 'The problem this tool answers'} />
              </Field>
              <Field label="How to use it" hint="One step per line.">
                <textarea className={TEXTAREA} rows={3} value={listing.process_steps} onChange={set('process_steps')} placeholder={'Open the tool\nPaste the input\nReview the result'} />
              </Field>
              <Field label="Example input" hint="Required. A listing publishes only once somebody can see what to put in.">
                <textarea className={TEXTAREA} rows={2} value={listing.example_input} onChange={set('example_input')} />
              </Field>
              <Field label="Example output" hint="Required. And what comes back out.">
                <textarea className={TEXTAREA} rows={2} value={listing.example_output} onChange={set('example_output')} />
              </Field>
              <Field label="Who else can use it">
                <Select value={listing.access_level} onChange={set('access_level')}>
                  <option value="request">Anyone in the company, on request</option>
                  <option value="open">Anyone in the company</option>
                  <option value="restricted">Only people granted access</option>
                </Select>
              </Field>
            </>
          )}

          <div className="flex flex-wrap items-center gap-2.5">
            <Action onClick={deliver} disabled={busy || !ready} reason={busy ? 'Publishing' : blocker}>
              Publish to the requester
            </Action>
            <Action variant="ghost" onClick={() => setMode('closed')}>Cancel</Action>
          </div>
        </div>
      )}
    </Card>
  );
};
